import { useCallback, useState } from 'react'
import { Inbox } from 'lucide-react'
import {
  Alert,
  Button,
  Badge,
  EmptyState,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  toast,
  FormattedDate,
  PageHeader,
} from '@ideon-api/ideon-ui'
import type { AuthPendingFinancePush } from '@ideon-api/sdk-internal'
import { useAuthServiceClient } from '../lib/sdk-client'
import { useSdkList } from '../lib/use-sdk-list'
import { BindAccountDialog } from './unlinked-sfdc/BindAccountDialog'

export function UnlinkedSfdcPage() {
  const getClient = useAuthServiceClient()
  const fetchPendingPushes = useCallback(async () => {
    const client = await getClient()
    return client.auth.listPendingFinancePushes()
  }, [getClient])
  const state = useSdkList<AuthPendingFinancePush>(fetchPendingPushes)
  const [dialogRowId, setDialogRowId] = useState<string | null>(null)
  // Client-side only -- a bound row isn't removed from this table by this UI.
  // Finance::PendingPushDrainJob is the one that eventually clears it (once
  // it destroys the now-redundant row); until then this just marks the row
  // so the admin doesn't re-attempt a bind that already succeeded.
  const [settledRowIds, setSettledRowIds] = useState<Set<string>>(new Set())

  const dialogRow =
    state.status === 'ready' ? state.data.find((row) => row.id === dialogRowId) ?? null : null

  const handleActionClick = (row: AuthPendingFinancePush) => {
    if (row.status !== 'pending') return
    setDialogRowId(row.id)
  }

  const handleBound = (rowId: string) => (result: { bound: boolean }) => {
    // Only a real write (bound: true) puts the row on the drain job's path
    // out of this queue. `bound: false` means the identifier already
    // resolved to the chosen account without this call changing anything --
    // settling the row here would hide a dead end (e.g. the existing link is
    // on a different `environment`) behind a state that looks like progress.
    if (result.bound) {
      setSettledRowIds((prev) => {
        const next = new Set(prev)
        next.add(rowId)
        return next
      })
    }
    toast({
      title: result.bound ? 'Account bound' : 'Already bound',
      description: result.bound
        ? "This row will drop off the queue once the drain job's next pass runs."
        : 'That identifier was already bound to the selected account -- no change was made.',
    })
  }

  return (
    <>
      <PageHeader title="Unlinked SFDC" subtitle="Finance pushes still waiting on an account match." />

      <div className="flex flex-col gap-6">
        {state.status === 'loading' && (
          <Table density="compact" variant="bordered" className="bg-white">
            <TableHeader>
              <TableRow className="border-ideon-gray-30">
                <TableHead className="text-ideon-gray-70">SFDC ID</TableHead>
                <TableHead className="text-ideon-gray-70">ThreeScale App ID</TableHead>
                <TableHead className="text-ideon-gray-70">Status</TableHead>
                <TableHead className="text-ideon-gray-70">Last Error</TableHead>
                <TableHead className="text-ideon-gray-70">Created</TableHead>
                <TableHead className="text-ideon-gray-70">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableSkeleton columns={6} />
          </Table>
        )}
        {state.status === 'error' && (
          <Alert variant="error" action={<Button onClick={state.reload}>Retry</Button>}>
            Failed to load: {state.message}
          </Alert>
        )}
        {state.status === 'ready' && state.data.length === 0 && (
          <EmptyState className="bg-white" icon={<Inbox className="h-10 w-10" />} heading="No unlinked SFDC pushes right now" />
        )}
        {state.status === 'ready' && state.data.length > 0 && (
          <Table density="compact" variant="bordered" className="bg-white">
            <TableHeader>
              <TableRow className="border-ideon-gray-30">
                <TableHead className="text-ideon-gray-70">SFDC ID</TableHead>
                <TableHead className="text-ideon-gray-70">ThreeScale App ID</TableHead>
                <TableHead className="text-ideon-gray-70">Status</TableHead>
                <TableHead className="text-ideon-gray-70">Last Error</TableHead>
                <TableHead className="text-ideon-gray-70">Created</TableHead>
                <TableHead className="text-ideon-gray-70">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {state.data.map((p) => {
                const settled = settledRowIds.has(p.id)
                return (
                  <TableRow
                    key={p.id}
                    data-testid={`finance-push-row-${p.id}`}
                    data-bind-state={settled ? 'settling' : undefined}
                    className="border-ideon-gray-30"
                  >
                    <TableCell className="font-semibold text-ideon-gray-black">{p.sfdcId}</TableCell>
                    <TableCell className="text-ideon-gray-black">{p.threescaleAppId}</TableCell>
                    <TableCell>
                      <Badge
                        variant={settled ? 'neutral' : p.status === 'conflict' ? 'error' : 'warning'}
                        dot={false}
                        uppercase={false}
                      >
                        {settled ? 'settling' : p.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-ideon-gray-70">{p.lastError ?? '—'}</TableCell>
                    <TableCell>
                      <FormattedDate value={p.createdAt} />
                    </TableCell>
                    <TableCell>
                      {p.status === 'conflict' ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              aria-disabled="true"
                              data-testid={`bind-action-${p.id}`}
                              onClick={() => handleActionClick(p)}
                              className="opacity-50 cursor-not-allowed"
                            >
                              Bind
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            This row&apos;s threescale_app_id already resolves to an account --
                            binding it again would be a no-op. The real blocker is that
                            account&apos;s sfdc_id disagreeing with this row&apos;s, which this
                            action can&apos;t fix. Resolve the sfdc_id conflict manually first.
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          data-testid={`bind-action-${p.id}`}
                          disabled={settled}
                          onClick={() => handleActionClick(p)}
                        >
                          {settled ? 'Bound' : 'Bind'}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        )}
      </div>

      {dialogRow && (
        <BindAccountDialog
          isOpen
          onClose={() => setDialogRowId(null)}
          threescaleAppId={dialogRow.threescaleAppId}
          onBound={handleBound(dialogRow.id)}
        />
      )}
    </>
  )
}
